import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api, { API_BASE } from '../api';
import gbahsLogo from '../img/gbahs.png';

const API_ORIGIN = API_BASE.replace(/\/api$/, '');

const AVATAR_EMOJIS = [
  '👨‍🏫', '👩‍🏫', '🧑‍🏫', '😊', '🙂', '😎', '🤓', '🧑', '👨', '👩',
  '👨‍💼', '👩‍💼', '🧔', '👴', '👵', '🐱', '🐶', '🦊', '🐻', '🐰',
];

const CHAT_POLL_MS = 15000;

const MENUS = [
  { key: 'dashboard', label: '대시보드', roles: ['admin', 'staff', 'teacher', 'foreign'] },
  {
    key: 'ato-management',
    label: 'ATO관리',
    roles: ['admin', 'staff'],
    children: [
      { key: 'ato-certificate', label: 'ATO 지정서', roles: ['admin', 'staff'] },
      { key: 'training-standard', label: '훈련운영기준', roles: ['admin', 'staff'] },
      { key: 'education-standard', label: '교육규정', roles: ['admin', 'staff'] },
      { key: 'teacher-qualification', label: '교사자격관리', roles: ['admin', 'staff'] },
    ],
  },
  {
    key: 'lesson-plan-management',
    label: '교육계획',
    roles: ['admin', 'staff', 'teacher', 'foreign'],
    children: [
      { key: 'academic-calendar', label: '학사일정', roles: ['admin', 'staff', 'teacher', 'foreign'] },
      { key: 'course-planning', label: '과정기획', roles: ['admin', 'staff'] },
      { key: 'lesson-plans', label: '교수계획', roles: ['admin', 'staff', 'teacher', 'foreign'] },
      { key: 'assignments', label: '정규수업배정', roles: ['admin', 'staff'] },
      { key: 'supplementary-assignment', label: '보충수업배정', roles: ['admin', 'staff'] },
    ],
  },
  {
    key: 'class-management',
    label: '교육운영',
    roles: ['admin', 'staff', 'teacher', 'foreign'],
    children: [
      { key: 'attendance', label: '출결관리', roles: ['admin', 'staff', 'teacher', 'foreign'] },
      { key: 'attendance-search', label: '출결조회', roles: ['admin', 'staff', 'teacher', 'foreign'] },
      { key: 'class-status', label: '수업현황', roles: ['admin', 'staff', 'teacher', 'foreign'] },
      { key: 'absence-status', label: '결시현황', roles: ['admin', 'staff'] },
    ],
  },
  {
    key: 'education-result',
    label: '교육결과',
    roles: ['admin', 'staff', 'teacher', 'foreign'],
    children: [
      { key: 'student-hours', label: '교육시수', roles: ['admin', 'staff'] },
      { key: 'education-days', label: '교육일수', roles: ['admin', 'staff'] },
      { key: 'class-hours', label: '반별시수', roles: ['admin', 'staff', 'teacher', 'foreign'] },
      { key: 'exam-results', label: '평가결과', roles: ['admin', 'staff', 'teacher', 'foreign'] },
    ],
  },
  {
    key: 'course-completion',
    label: '과정수료',
    roles: ['admin', 'staff'],
    children: [
      { key: 'exam-summary', label: '교육결과종합', roles: ['admin', 'staff'] },
      { key: 'completion-certificate', label: '교육이수증명', roles: ['admin', 'staff'] },
    ],
  },
  {
    key: 'data-management',
    label: '자료관리',
    roles: ['admin', 'staff'],
    children: [
      { key: 'subject-management', label: '지정과목', roles: ['admin', 'staff'] },
      { key: 'material-management', label: '교재관리', roles: ['admin', 'staff'] },
      { key: 'student-management', label: '학생관리', roles: ['admin', 'staff'] },
      { key: 'teacher-management', label: '교사관리', roles: ['admin', 'staff'] },
      { key: 'data-files', label: '자료관리', roles: ['admin', 'staff'] },
    ],
  },
  {
    key: 'personal-data',
    label: '개인자료',
    roles: ['admin', 'staff', 'teacher', 'foreign'],
    children: [
      { key: 'teacher-schedule', label: '나의일정', roles: ['admin', 'staff', 'teacher', 'foreign'] },
      { key: 'youtube-material', label: '교육자료관리', roles: ['admin', 'staff', 'teacher', 'foreign'] },
      { key: 'my-profile', label: '개인정보', roles: ['admin', 'staff', 'teacher', 'foreign'] },
    ],
  },
  { key: 'chat', label: '채팅', roles: ['admin', 'staff', 'teacher', 'foreign'] },
];

// 홈(대시보드) 메뉴용 단색 선 집 아이콘 (Tabler Icons "home", MIT). 글자색(currentColor)을 따라가
// 평소에는 연한 하늘색, 마우스를 올리거나 선택되면 흰색이 된다.
function HomeIcon() {
  return (
    <svg
      className="topnav-home-icon"
      viewBox="0 0 24 24"
      width="24"
      height="24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12l-2 0l9 -9l9 9l-2 0" />
      <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-7" />
      <path d="M9 21v-6a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v6" />
    </svg>
  );
}

export default function TopNav({ active, onNavigate }) {
  const { user, logout, refreshMe } = useAuth();
  const [openMenu, setOpenMenu] = useState(null);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navRef = useRef(null);
  const avatarRef = useRef(null);
  const photoInputRef = useRef(null);
  const mobileMenuRef = useRef(null);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const handleOutside = (e) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target)) setMobileMenuOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (!avatarOpen) return undefined;
    const handleOutside = (e) => {
      if (avatarRef.current && !avatarRef.current.contains(e.target)) setAvatarOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [avatarOpen]);

  const pickAvatar = async (emoji) => {
    await api.post('/me/avatar', { avatar: emoji });
    await refreshMe();
    setAvatarOpen(false);
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await api.post('/me/avatar/photo', form);
      await refreshMe();
      setAvatarOpen(false);
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async () => {
    await api.delete('/me/avatar/photo');
    await refreshMe();
  };

  // 채팅 새 글 개수: 마지막으로 본 글 번호를 이 브라우저(localStorage)에 기억해 두고,
  // 그 뒤에 다른 사람이 올린 글 수를 주기적으로 물어본다. 채팅 화면에 있는 동안에는 모두 읽은 것으로 본다.
  const [chatUnread, setChatUnread] = useState(0);
  const chatSeenKey = user?.teachid ? `chat_seen_${user.teachid}` : null;
  const onChat = active === 'chat';
  const wasOnChatRef = useRef(false);

  useEffect(() => {
    if (!chatSeenKey) return undefined;
    const readSeen = () => {
      try {
        const v = localStorage.getItem(chatSeenKey);
        return v === null ? null : Number(v);
      } catch (e) {
        return null;
      }
    };
    const writeSeen = (id) => {
      try {
        localStorage.setItem(chatSeenKey, String(id));
      } catch (e) {
        /* 저장할 수 없는 환경이면 새로고침 때마다 0부터 센다 */
      }
    };
    let cancelled = false;
    // 채팅 화면에서 막 나온 경우, 그 첫 조회에서는 채팅에서 본 글까지를 읽은 것으로 기록한다
    let justLeftChat = wasOnChatRef.current && !onChat;
    wasOnChatRef.current = onChat;

    const check = async () => {
      try {
        const seen = readSeen();
        const res = await api.get('/chat/unread', { params: seen === null ? {} : { after_id: seen } });
        if (cancelled) return;
        const { latest_id: latestId, unread_count: count } = res.data;
        const markAllSeen = onChat || seen === null || justLeftChat;
        justLeftChat = false;
        if (markAllSeen) {
          // 채팅을 보고 있거나 처음 접속한 경우: 지금까지의 글은 읽은 것으로 간주
          writeSeen(latestId);
          setChatUnread(0);
        } else {
          setChatUnread(count);
        }
      } catch (e) {
        /* 일시적인 네트워크 오류는 다음 주기에 다시 시도 */
      }
    };

    check();
    const timer = setInterval(check, CHAT_POLL_MS);
    const handleVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', handleVisible);
    window.addEventListener('focus', handleVisible);
    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisible);
      window.removeEventListener('focus', handleVisible);
    };
  }, [chatSeenKey, onChat]);

  const chatBadge = (n) => (n > 0 ? <span className="topnav-badge">{n > 99 ? '99+' : n}</span> : null);

  const visibleMenus = MENUS.filter((m) => m.roles.includes(user?.role)).map((m) =>
    m.children ? { ...m, children: m.children.filter((c) => c.roles.includes(user?.role)) } : m
  );

  useEffect(() => {
    const handleOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  const handleClick = (m) => {
    if (m.children) {
      setOpenMenu((prev) => (prev === m.key ? null : m.key));
    } else {
      onNavigate(m.key);
      setOpenMenu(null);
    }
  };

  // 평가결과입력 페이지는 메뉴에 없고 평가결과조회의 [입력] 버튼으로 들어오므로 교육결과 메뉴를 활성으로 표시한다
  const isParentActive = (m) => m.children?.some((c) => c.key === active) || (m.key === 'education-result' && active === 'exam-scores');

  return (
    <header className="topnav">
      <div className="topnav-brand" translate="no">
        <img src={gbahsLogo} alt="" className="topnav-logo" />
        경북항공기술교육원 운영관리
      </div>
      <nav className="topnav-menu" ref={navRef}>
        {visibleMenus.map((m) => (
          <div key={m.key} className="topnav-menu-group">
            <button
              className={`topnav-item ${m.key === 'dashboard' ? 'topnav-home' : ''} ${active === m.key || isParentActive(m) ? 'active' : ''}`}
              onClick={() => handleClick(m)}
              {...(m.key === 'dashboard' ? { title: '홈', 'aria-label': '홈' } : {})}
            >
              {m.key === 'dashboard' ? <HomeIcon /> : m.label}
              {m.key === 'chat' && chatBadge(chatUnread)}
              {m.children && <span className="topnav-caret">▾</span>}
            </button>
            {m.children && openMenu === m.key && (
              <div className="topnav-dropdown">
                {m.children.map((c) => (
                  <button
                    key={c.key}
                    className={`topnav-dropdown-item ${active === c.key ? 'active' : ''}`}
                    onClick={() => {
                      onNavigate(c.key);
                      setOpenMenu(null);
                    }}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>

      <div className="topnav-user">
        <div className="topnav-avatar-wrap" ref={avatarRef}>
          <button
            className="topnav-avatar-btn"
            title="아바타 바꾸기"
            onClick={() => setAvatarOpen((v) => !v)}
          >
            {user?.avatar_photo_url ? (
              <img src={`${API_ORIGIN}${user.avatar_photo_url}`} alt="" className="topnav-avatar-img" />
            ) : (
              user?.avatar || '🙂'
            )}
          </button>
          {avatarOpen && (
            <div className="topnav-avatar-panel">
              <div className="topnav-avatar-photo-row">
                <button
                  type="button"
                  className="btn-ghost topnav-avatar-photo-btn"
                  onClick={() => photoInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? '업로드 중...' : '📷 내 사진 올리기'}
                </button>
                {user?.avatar_photo_url && (
                  <button type="button" className="btn-ghost topnav-avatar-photo-btn" onClick={removePhoto}>
                    사진 삭제
                  </button>
                )}
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handlePhotoChange}
                />
              </div>
              <div className="chat-theme-section-label">또는 이모지 선택</div>
              <div className="topnav-avatar-grid">
                {AVATAR_EMOJIS.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    className={`topnav-avatar-option${!user?.avatar_photo_url && user?.avatar === emoji ? ' active' : ''}`}
                    onClick={() => pickAvatar(emoji)}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <span className="topnav-user-name">{user?.name} 선생님</span>
        <button className="btn-ghost" onClick={logout}>로그아웃</button>
      </div>

      <div className="topnav-mobile-wrap" ref={mobileMenuRef}>
        <button
          type="button"
          className="topnav-mobile-toggle"
          title="메뉴"
          onClick={() => setMobileMenuOpen((v) => !v)}
        >
          ⋮
        </button>
        {mobileMenuOpen && (
          <div className="topnav-dropdown topnav-mobile-panel">
            {visibleMenus.map((m) =>
              m.children ? (
                <React.Fragment key={m.key}>
                  <div className="topnav-mobile-group-label">{m.label}</div>
                  {m.children.map((c) => (
                    <button
                      key={c.key}
                      className={`topnav-dropdown-item topnav-mobile-child ${active === c.key ? 'active' : ''}`}
                      onClick={() => {
                        onNavigate(c.key);
                        setMobileMenuOpen(false);
                      }}
                    >
                      {c.label}
                    </button>
                  ))}
                </React.Fragment>
              ) : (
                <button
                  key={m.key}
                  className={`topnav-dropdown-item ${active === m.key ? 'active' : ''}`}
                  onClick={() => {
                    onNavigate(m.key);
                    setMobileMenuOpen(false);
                  }}
                >
                  {m.key === 'dashboard' ? (
                    <span className="topnav-home-row">
                      <HomeIcon />
                      {m.label}
                    </span>
                  ) : (
                    m.label
                  )}
                  {m.key === 'chat' && chatBadge(chatUnread)}
                </button>
              )
            )}
            <div className="topnav-mobile-divider" />
            <button className="topnav-dropdown-item" onClick={logout}>로그아웃</button>
          </div>
        )}
      </div>
    </header>
  );
}
